//ReporterOverview.tsx
import * as React from "react";
import { getAwaitingReportData } from "../../../service/AwaitingReportService";
import { AwaitingReportType } from "../data/AwaitingReportType";
import {
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import theme from "../../../style/theme";
import ReportActivityStepper from "./ReportActivityStepper";
import { useNavigate } from "react-router-dom";
import NotificationImportantRoundedIcon from "@mui/icons-material/NotificationImportantRounded";
import { useUser } from "../../../context/UserContext";
import ClearSharpIcon from "@mui/icons-material/ClearSharp";
import dayjs from "dayjs";

/**
 * 報告待ち一覧コンポーネント
 * - 報告待ちの申請を一覧表示し、検索・ソート・無限スクロールを提供する
 * - ログインユーザーが報告者として設定されている申請のみ表示する
 */
const ReporterOverview: React.FC = () => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const { user } = useUser();
  const userId = user?.id;
  const navigate = useNavigate();
  const [visibleRecords, setVisibleRecords] = React.useState<
    AwaitingReportType[]
  >([]); // 表示する記録
  const [loading, setLoading] = React.useState<boolean>(false); // ローディング中か否か
  const [allLoaded, setAllLoaded] = React.useState(false); // すべての記録をローディング完了か否か
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">(() => {
    // 順序並び方(ボタンを1回でも押している場合は、sessionStorageから復元)
    const saved = sessionStorage.getItem("sortOrder_Awaiting");
    return saved === "asc" || saved === "desc" ? saved : "desc";
  });
  const [filterText, setFilterText] = React.useState<string>(() => {
    // テキストフィルター(テキストが入っている場合は、sessionStorageから復元)
    return sessionStorage.getItem("filterText_Awaiting") || "";
  });
  const [loadedCount, setLoadedCount] = React.useState<number>(0); // ローディングされた記録数
  const [allRecords, setAllRecords] = React.useState<AwaitingReportType[]>([]); // 全部のデータ
  const statusMapping: { [key: string]: string } = {
    //申請ステータスの日本語マーピング
    InProgress: "申請中",
    Completed: "完了",
    Canceled: "取り下げ",
    SendBack: "差し戻し",
    Failed: "却下",
  };
  //フィルター選択肢
  const [filterOptions, setFilterOptions] = React.useState<{
    [key: string]: boolean;
  }>(() => {
    // sessionStorageにフィルター選択肢の値が残っている場合は、sessionStorage から復元。それ以外は初期値。
    const saved = sessionStorage.getItem("filterOptions_Awaiting");
    return saved
      ? JSON.parse(saved)
      : {
        pendingApproval: false, //デフォルト的に「申請中」を選ばない
        urgentRequest: false, // デフォルト的に「急ぎ申請」を選ばない
        sendBack: false, // デフォルト的に「差し戻し」を選ばない
        completed: false, // デフォルト的に「完了」を選ばない
        allState: true, // デフォルト的に「すべて」(状態)を選ぶ
        allUrgency: false, //デフォルト的に「すべて」(緊急度)を選ばない
        isAwaitingReport: false, //デフォルト的に「応答待ち」を選ばない
        reported: true, // デフォルト的に「応答済み」を選ぶ
        allReport: false, //// デフォルト的に「すべて」(応答状況)を選ばない
      };
  });

  // フィルターラベルを定義
  const filterLabels = [
    { category: "ステータス：", key: "pendingApproval", label: "申請中" },
    { category: "ステータス：", key: "completed", label: "完了" },
    { category: "ステータス：", key: "sendBack", label: "差し戻し" },
    { category: "ステータス：", key: "allState", label: "すべて" },
    // { category: '緊急度:',key: 'urgentRequest', label: '急ぎ申請' },
    // { category: '緊急度:',key: 'allUrgency', label: 'すべて' },
    { category: "応答状況：", key: "isAwaitingReport", label: "応答待ち" },
    { category: "応答状況：", key: "reported", label: "応答済み" },
    { category: "応答状況：", key: "allReport", label: "すべて" },
  ];

  // 応答待ち：
  const isAwaitingReport = (
    record: AwaitingReportType,
    userId: string
  ): boolean => {
    return record.activities.some(
      (activity) =>
        activity.code === "4" &&
        activity.activityStatus === "InProgress" &&
        activity.reporterIds.includes(userId) && //ログイン中のユーザは報告者と設定されている
        !activity.reportedIds.includes(userId) //ログイン中のユーザが報告したことがない
    );
  };

  // 応答済み：
  const isReported = (record: AwaitingReportType, userId: string): boolean => {
    // 該当のアクティビティ（code=4 かつ reporterIds に含まれる）を抽出
    const relevantActivities = record.activities.filter(
      (activity) =>
        activity.code === "4" && activity.reporterIds.includes(userId)
    );
    // 1件もない場合は「応答済み」ではない
    if (relevantActivities.length === 0) return false;
    // 全てにおいて reportedIds にも含まれている（全部応答済）
    return relevantActivities.every((activity) =>
      activity.reportedIds.includes(userId)
    );
  };

  // 遷移後画面
  const handleCardClick = (record: AwaitingReportType) => {
    navigate(`/detail/${record.id}`, {
      state: { ...record, from: "ReporterOverview" },
    });
  };

  // **1. filterOptions(フィルターラベル)の変化を監視してデータを再取得**
  React.useEffect(() => {
    const fetchData = async () => {
      if (!userId) return; //未ログインの際は処理しない
      setLoading(true);
      try {
        const data = await getAwaitingReportData();
        setAllRecords(data);
        setLoadedCount(0);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [filterOptions, userId]);

  // **2.フィルター条件設定：申請ステータスによって分類する**
  const filteredRecords = React.useMemo(() => {
    if (!userId) return [];
    return allRecords.filter((record) => {
      let matches = true;
      if (!filterOptions.allState) {
        if (filterOptions.pendingApproval)
          matches = matches && record.applicationStatus === "InProgress";
        if (filterOptions.completed)
          matches = matches && record.applicationStatus === "Completed";
        if (filterOptions.sendBack)
          matches = matches && record.applicationStatus === "SendBack";
      }
      // if (!filterOptions.allUrgency) {
      //   if (filterOptions.urgentRequest) matches = matches && record.isUrgent === true;
      // }
      if (!filterOptions.allReport) {
        if (filterOptions.isAwaitingReport) {
          matches = matches && isAwaitingReport(record, userId);
        }
        if (filterOptions.reported) {
          matches = matches && isReported(record, userId);
        }
      }

      //テキストで記録をフィルタリングする
      if (filterText.trim() !== "") {
        const recordText =
          `${record.applicationNumber} ${record.applicationType} ${record.applicationFormat} ${dayjs(record.submittedAt).isValid() ? dayjs(record.submittedAt).format("YYYY/MM/DD") : "未提出"} ${record.applicant} ${record.userProfiles} ${record.applicationStatus}`.toLowerCase();
        matches = matches && recordText.includes(filterText.toLowerCase());
      }

      return matches;
    });
  }, [allRecords, filterOptions, filterText]);

  // **2. フィルタリングした記録の並び順**
  const sortedFilteredRecords = React.useMemo(() => {
    return [...filteredRecords].sort((a, b) => {
      // デフォルト的に日付の降順で並ぶ
      return sortOrder === "desc"
        ? new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
        : new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime();
    });
    // .sort((a, b) => {
    //   // 急ぎ申請は上に表示
    //   if (a.isUrgent === true && b.isUrgent === false) return -1;
    //   if (b.isUrgent === true && a.isUrgent === false) return 1;
    //   return 0;
    // });
  }, [filteredRecords, sortOrder]);

  // **3. sortedFilteredRecords(labelでフィリングしたfilteredRecordsと並び順sortOrderで決める)の変化を監視して表示する記録を更新**
  React.useEffect(() => {
    const initialRecords = sortedFilteredRecords.slice(0, 3);
    setVisibleRecords(initialRecords);
    setLoadedCount(initialRecords.length);
    setAllLoaded(initialRecords.length >= sortedFilteredRecords.length);
  }, [sortedFilteredRecords]);

  // **4. labelをクリックする**

  const handleTagClick = (optionKey: string) => {
    // 対象カテゴリを取得
    const targetCategory = filterLabels.find((label) => label.key === optionKey)?.category;
    if (!targetCategory) return;

    setFilterOptions((prevOptions) => {
      // 対象カテゴリに属するキー一覧
      const categoryKeys = filterLabels
        .filter((label) => label.category === targetCategory)
        .map((label) => label.key);

      const isCurrentlyOn = !!prevOptions[optionKey];
      const onCountInCategory = categoryKeys.reduce(
        (acc, key) => acc + (prevOptions[key] ? 1 : 0),
        0
      );

      // 新しいオプションのベース
      const updatedOptions = { ...prevOptions };

      // --- ラジオ風の動作 ---
      // 1) クリックしたキーを常にONにする
      // 2) 同一カテゴリの他キーはすべてOFFにする
      // 3) ただし、"最後の1個をOFF" にする操作は許可しない（= 再クリックでもOFFにならない）
      if (isCurrentlyOn && onCountInCategory === 1) {
        // クリックしたキーがカテゴリで唯一のONの場合は、OFFにしない（変更なし）
        return prevOptions;
      }

      // クリックしたキーをON
      updatedOptions[optionKey] = true;

      // 同カテゴリの他キーをOFF
      categoryKeys
        .filter((key) => key !== optionKey)
        .forEach((key) => {
          updatedOptions[key] = false;
        });

      // sessionStorageに保存
      sessionStorage.setItem("filterOptions_Awaiting", JSON.stringify(updatedOptions));
      return updatedOptions;
    });
  };


  // **5.もっと多くの記録をローディング**
  const loadMoreRecords = () => {
    if (loading || allLoaded) return;
    setLoading(true);
    setTimeout(() => {
      const nextRecords = sortedFilteredRecords.slice(0, loadedCount + 3);
      setVisibleRecords(nextRecords);
      setLoadedCount(nextRecords.length);
      setLoading(false);
      setAllLoaded(nextRecords.length >= sortedFilteredRecords.length);
    }, 500);
  };

  // **6.  スクロールイベントを監視する**
  React.useEffect(() => {
    const handleScroll = () => {
      // 現在のスクロール位置とページの高さを取得する
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.body.offsetHeight;

      if (scrollPosition >= documentHeight - 100) {
        // ページの底から100pxの位置で、さらに読み込む処理を実行する
        if (!loading && visibleRecords.length < sortedFilteredRecords.length) {
          console.log("Start loading more records...");
          loadMoreRecords();
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, visibleRecords, sortedFilteredRecords.length, loadMoreRecords]);

  // **7. (日付による)並び順を変更**
  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => {
      const newOrder = prevOrder === "desc" ? "asc" : "desc";
      sessionStorage.setItem("sortOrder_Awaiting", newOrder); // 並び順をsessionStorageに保存
      return newOrder;
    });
  };

  // フィルターの条件を初期化する
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("filterOptions_Awaiting");
    sessionStorage.removeItem("sortOrder_Awaiting");
    sessionStorage.removeItem("filterText_Awaiting");

    // フィルター条件の初期化
    setFilterText("");
    setSortOrder("desc");
    setFilterOptions({
      pendingApproval: false, //デフォルト的に「申請中」を選ばない
      urgentRequest: false, // デフォルト的に「急ぎ申請」を選ばない
      completed: false, // デフォルト的に「完了」を選ばない
      allState: true, // デフォルト的に「すべて」(状態)を選ぶ
      sendBack: false, // デフォルト的に「差し戻し」(状態)を選ばない
      allUrgency: false, //デフォルト的に「すべて」(緊急度)を選ばない
      isAwaitingReport: false, //デフォルト的に「応答待ち」を選ばない
      reported: true, // デフォルト的に「応答済み」を選ぶ
      allReport: false, //// デフォルト的に「すべて」(応答状況)を選ばない
    });
  };

  // テキストボックスのテキストをクリア
  const handleFilterTextClear = () => {
    // フィルター検索のテキストを初期化
    setFilterText("");
    // sessionStorageの値をクリアする
    sessionStorage.removeItem("filterText_Awaiting");
  };

  //Loading表示
  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <CircularProgress />
      </div>
    );
  };

  return (
    <Box>
      {/* フィルタ入力 */}
      <Box display="flex" alignItems="center" sx={{ m: 2 }}>
        <TextField
          label="フィルタを入力"
          variant="outlined"
          size="small"
          value={filterText}
          onChange={(e) => {
            const newValue = e.target.value;
            setFilterText(newValue);
            sessionStorage.setItem("filterText_Awaiting", newValue); // 入力したテキストをsessionStorageに格納する。
          }}
          sx={{ ml: 1, width: 220 }}
          slotProps={{
            input: {
              endAdornment: filterText && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleFilterTextClear}
                    edge="end"
                    sx={{
                      backgroundColor: "transparent", // 背景透明
                      "&:hover": {
                        backgroundColor: "transparent", // ホバー時も透明
                      },
                    }}
                  >
                    <ClearSharpIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        {/* 順序並びボタン */}
        <Button onClick={toggleSortOrder}>
          {sortOrder === "desc" ? "申請日 昇順" : "申請日 降順"}
        </Button>
      </Box>

      {/* フィルター機能 */}
      <Box
        sx={{
          display: "flex",
          ml: 1,
          gap: 2,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* {["ステータス：", "緊急度:"].map((category) => ( */}
        {["ステータス：", "応答状況："].map((category) => (
          <Box
            key={category}
            sx={{
              mb: isMobile ? 0 : 2,
              display: "flex",
              ml: 2,
              alignItems: "center",
            }}
          >
            <Box display="flex" sx={{ gap: 1, flexWrap: isMobile ? 'wrap' : 'nowrap', maxWidth: isMobile ? '327px' : 'none' }}>
              {/* フィルターラベル */}
              <Typography noWrap={isMobile} variant="subtitle1" sx={{ mr: 1 }}>
                {category}
              </Typography>
              {/* <Box display="flex" sx={{ gap: 1 }}> */}
              {filterLabels
                .filter((label) => label.category === category)
                .map(({ key, label }) => (
                  <Chip
                    key={key}
                    label={label}
                    clickable
                    onClick={() => handleTagClick(key)}
                    sx={{
                      backgroundColor: filterOptions[key]
                        ? theme.palette.custom.blue
                        : "grey.200",
                      color: "text.primary",
                      "&:hover": {
                        backgroundColor: filterOptions[key]
                          ? "primary.dark"
                          : "grey.400",
                      },
                    }}
                  />
                ))}
            </Box>
          </Box>
        ))}
        {/* フィルターのリセット機能(初期化して、sessionStorageをclearにする) */}
        <Box
          sx={{
            mb: isMobile ? 0 : 2,
            display: "flex",
            ml: 2,
            alignItems: "center",
          }}
        >
          <Chip
            label="リセット"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
            onClick={filterReset}
          />
        </Box>
      </Box>
      {/* 申請記録カード */}
      {visibleRecords.map((record) => (
        <Card
          key={record.id}
          sx={{
            margin: 3,
            cursor: "pointer",
            transition: "transform 0.2s, box-shadow 0.2s",
            "&:hover": {
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#f9f9f9",
            },
          }}
          onClick={(event) => {
            event.stopPropagation(); // クリックエリアでイベントバブリング停止
            handleCardClick(record);
          }}
        >
          <CardContent>
            {/* 申請番号 */}
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}
            >
              {/*申請番号 */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label={`申請番号: ${record.applicationNumber}`}
                  color="primary"
                  sx={{}}
                />
                <Chip
                  label={`申請日：${dayjs(record.submittedAt).isValid() ? dayjs(record.submittedAt).format("YYYY/MM/DD") : "未提出"}`}
                  variant="outlined"
                  color="success"
                />
              </Box>
              {/* 急ぎ申請マーク */}
              <Box sx={{ mr: 1 }}>
                {record.isUrgent === true && (
                  <Box sx={{ mt: 1, color: "orange" }}>
                    <NotificationImportantRoundedIcon />
                  </Box>
                )}
              </Box>
            </Box>
            {/* 申請名*/}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                mb: 0.5,
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography variant="h3" align="center" sx={{}}>
                  {record.applicationType}
                </Typography>
                <Typography variant="h3" align="center" sx={{}}>
                  :
                </Typography>
                <Typography
                  variant="h3"
                  align="center"
                  color="textSecondary"
                  sx={{}}
                >
                  {record.applicationFormat}
                </Typography>
              </Box>
              <Typography variant="body1" align="center">
                {record.description}
              </Typography>
            </Box>
            {/* テーブル */}
            <TableContainer>
              <Table
                size="small"
                aria-label="application details table"
                sx={{}}
              >
                {/* 動的にTableHeadを生成 */}
                <TableHead>
                  <TableRow sx={{}}>
                    <TableCell sx={{ textAlign: "center", width: "33.3%" }}>
                      <Typography variant="body1">申請者</Typography>
                    </TableCell>
                    {/* userProfiles の key を動的に取得 */}
                    {record.userProfiles.map((profile) => (
                      <TableCell key={profile.key} sx={{ textAlign: "center" }}>
                        <Typography variant="body1">{profile.key}</Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: "center", width: "33.3%" }}>
                      <Typography variant="body1">ステータス</Typography>
                    </TableCell>
                    {record.applicationStatus === "InProgress" && (
                      <TableCell sx={{ textAlign: "center" }}>
                        <Typography variant="body1">進行中アクティビティ</Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                {/* 動的にTableBodyを生成 */}
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ textAlign: "center", width: "33.3%" }}>
                      <Typography variant="body2">
                        {record.applicant}
                      </Typography>
                    </TableCell>
                    {/* userProfiles の value を動的に取得 */}
                    {record.userProfiles.map((profile) => (
                      <TableCell key={profile.key} sx={{ textAlign: "center" }}>
                        <Typography variant="body2">{profile.value}</Typography>
                      </TableCell>
                    ))}
                    <TableCell sx={{ textAlign: "center", width: "33.3%" }}>
                      <Typography variant="body2">
                        {statusMapping[record.applicationStatus] ||
                          record.applicationStatus}
                      </Typography>
                    </TableCell>
                    {record.applicationStatus === "InProgress" && (
                      <TableCell sx={{ textAlign: "center" }}>
                        <Typography variant="body2">
                          {record.activities?.slice()
                            .filter((item) => { return item.activityStatus === 'InProgress'; })
                            ?.filter((item, index) => { return index === 0; })
                            .map((item) =>
                              item.activityName
                            )}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {/* 申請進捗 */}
            <ReportActivityStepper record={record} />
          </CardContent>
        </Card>
      ))}

      {allLoaded && (
        <Typography variant="body1" sx={{ marginTop: 2, textAlign: "center" }}>
          これ以上ありません！
        </Typography>
      )}
    </Box>
  );
};

export default ReporterOverview;
